import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { AuthenticationService, ModalService } from '@core/services';
import { LocaleService } from '@selfcare/cms';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { UserActions, userFeature } from '@src/state';
import { AppRouting } from '@core/enums';
import { TranslatePipe } from '@core/pipes';
import { ButtonType } from '@core/models';
import { Router } from '@angular/router';
import { OptionComponent } from '../option/option.component';
import { SelectComponent } from '../select/select.component';
import { ListModalComponent } from '../list-modal/list-modal.component';
import { tap } from 'rxjs';
import { RemoveTermShorthandPipe } from '@core/pipes/remove-term-shorthand.pipe';
import { MsisdnRadioButtonListComponent } from '../msisdn-radio-button-list/msisdn-radio-button-list.component';
import { ButtonComponent } from '../button/button.component';
import { SvgComponent } from '../svg/svg.component';
import { ClickToCopyDirective } from '@core/directives';
import { ACTIVE_PRODUCT_LOCALSTORAGE_KEY, CURRENT_MSISDN_LOCALSTORAGE_KEY } from '@core/const/const';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { environment } from '@src/environments/environment';
import { getProductConfig } from '@core/helpers';
import { ProductType } from '@core/contracts/data-contracts';

@UntilDestroy()
@Component({
  selector: 'sc-mobile-profile',
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    SvgComponent,
    TranslatePipe,
    ButtonComponent,
    OptionComponent,
    SelectComponent,
    CommonModule,
    RemoveTermShorthandPipe,
    MsisdnRadioButtonListComponent,
    ClickToCopyDirective,
  ],
  providers: [AsyncPipe],
  templateUrl: './mobile-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileProfileComponent implements OnInit {
  @ViewChild('scrollContainer') scrollContainerRef!: ElementRef<HTMLElement>;
  @ViewChild(MsisdnRadioButtonListComponent) msisdnListCmp!: MsisdnRadioButtonListComponent;

  public readonly mainUrl = `${environment.mainUrl}/place-order`;

  public localeList$ = this.localeService.locales$;
  public form = this.fb.group({
    languageCode: this.localeService.currentLocale,
  });

  public isOpen = output<boolean>();
  public user = this.store.selectSignal(userFeature.selectCurrent);
  public userNameDetails = this.store.selectSignal(userFeature.selectUserNameDetails);
  public userInitials = this.store.selectSignal(userFeature.selectUserInitials);
  public isLimited = this.store.selectSignal(userFeature.selectIsMsisdnLimited);
  public isB2B = this.store.selectSignal(userFeature.selectIsB2B);
  public activeProduct = this.store.selectSignal(userFeature.selectActiveProduct);
  public products = this.store.selectSignal(userFeature.selectProducts);

  public selectedLanguage = signal<string>(this.localeService.currentLocale);

  public ButtonType = ButtonType;

  public readonly productConfig = computed(() => getProductConfig(this.activeProduct()));
  public isProductSwitchAvailable = computed(() => this.products().length > 1);

  constructor(
    private readonly fb: FormBuilder,
    private readonly localeService: LocaleService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly authenticationService: AuthenticationService,
    private readonly modalService: ModalService
  ) { }

  ngOnInit() {
    const menuBtn = document.querySelector('#burgerMenuToggler') as HTMLButtonElement;
    if (menuBtn.getAttribute('aria-expanded') === 'true') {
      menuBtn.click();
    }

    this.form.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((value) => this.localeService.changeLocale(value.languageCode))
      )
      .subscribe();
  }

  public signout() {
    localStorage.removeItem(CURRENT_MSISDN_LOCALSTORAGE_KEY);
    localStorage.removeItem(ACTIVE_PRODUCT_LOCALSTORAGE_KEY);
    this.authenticationService.logout();
    this.router.navigate([AppRouting.Login]);
  }

  public goToSettings() {
    this.isOpen.emit(false);
    this.router.navigate([AppRouting.Settings]);
  }

  public close() {
    this.isOpen.emit(false);
  }

  public outSelectedOption(lang: string) {
    this.selectedLanguage.set(lang);
  }

  public openList() {
    this.modalService.open(ListModalComponent);
    this.close();
  }

  public setActiveProduct(selectedProduct: ProductType) {
    this.store.dispatch(UserActions.setActiveProduct(selectedProduct));
    this.router.navigateByUrl(this.router.url, { onSameUrlNavigation: 'reload' });
  }
}
